import { readStepCatalogue, type StepCatalogue } from "../../../api/content/authoring/stepCatalogueApi";
import { builtInToolName, selectorVersionKey, type ToolSummary } from "../../../api/content/processDefinitions";
import { readItemListing, readProcessItem, readRoleBindings, type ListedItem } from "../../../api/content/processItemsApi";
import { listWorkflows } from "../../../api/content/workflowsApi";
import { resolveVersionLabels } from "../../versions/labels/useVersionLabels";
import type { ProcessReader } from "./processCatalogue";

/**
 * How the Workflow page and a Thread read a Workflow: floating selectors and roles resolve
 * to the latest version in `projectId`, or in its content version `commit`, the one a
 * Thread runs at (F103); without a project they stay unresolved. The
 * project's item listing is read once per load, for the latest versions, the role
 * bindings and the counts alike, and so is the step catalogue, for the built-in tools and
 * the selectors' value kinds.
 */
export function projectProcessReader(projectId: string | null, signal: AbortSignal, commit: string | null = null): ProcessReader {
  let listing: Promise<ReadonlyMap<string, ListedItem>> | null = null;
  const listed = () => {
    listing ??= projectId === null ? Promise.resolve(new Map()) : readItemListing(projectId, signal, commit);
    return listing;
  };
  let catalogue: Promise<StepCatalogue | null> | null = null;
  const stepCatalogue = () => {
    catalogue ??= projectId === null ? Promise.resolve(null) : readStepCatalogue(projectId, signal);
    return catalogue;
  };
  return {
    readItem: (item) => readProcessItem(item, signal),
    findLatestItems: async (itemIds) => new Map([...(await listed())].filter(([itemId]) => itemIds.has(itemId))),
    findRoleBindings: async () => readRoleBindings([...(await listed()).values()].filter((item) => item.kind === "artifact").map((item) => item.reference), signal),
    listWorkflows: async () => (projectId === null ? [] : (await listWorkflows(projectId, commit, signal)).workflows.map((workflow) => workflow.reference)),
    readBuiltInTools: async () => {
      const read = await stepCatalogue();
      return new Map(
        (read?.builtInTools ?? []).map((tool): [string, ToolSummary] => [
          tool.reference,
          {
            reference: tool.reference,
            name: builtInToolName(tool.reference) ?? tool.reference,
            outputs: Object.fromEntries(Object.entries(tool.interface.outputs).map(([name, output]) => [name, output.kind])),
            modelUse: tool.modelUse,
          },
        ]),
      );
    },
    readSelectorValueKinds: async () => new Map(((await stepCatalogue())?.selectors ?? []).map((selector) => [selectorVersionKey(selector), selector.valueKind])),
    countItemsByKind: async () => {
      const counts = new Map<string, number>();
      for (const item of (await listed()).values()) {
        counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);
      }
      return counts;
    },
    readVersions: async (references) => {
      if (projectId === null) return new Map();
      const labels = await resolveVersionLabels(projectId, { itemVersions: references, collectionVersions: [] }, signal);
      return new Map([...labels].flatMap(([reference, label]): [string, string][] => (label === null ? [] : [[reference, label.version]])));
    },
  };
}
